import React, { useState } from 'react';
import './Features.css';

const features = [
  {
    id: 'train',
    icon: '🏋️',
    title: 'AI Training Plans',
    tagline: 'Built around you. Evolving with you.',
    description:
      'FitMind AI builds a fully personalised training plan based on your goals, experience level, available equipment and schedule. During every session it suggests the exact weights to lift based on your previous performance — and after each month the AI recalibrates the whole plan.',
    bullets: [
      'Gym, home or hybrid training environments',
      'Per-session AI weight suggestions based on past lifts',
      'Exercise substitutions when equipment is unavailable',
      'Full plan view with detailed set/rep/weight targets',
    ],
    video: '/assets/videos2/train_session_screen_video.webm',
    image: '/assets/images2/train_screen.png',
    color: '#2371D8',
  },
  {
    id: 'coach',
    icon: '🧠',
    title: 'AI Coach Chat',
    tagline: 'Like having a personal trainer on speed dial.',
    description:
      'Ask anything — form tips, nutrition advice, deload guidance, motivation. Your AI Coach has full context of your history, current plan and recent check-ins. Every answer is tailored to your data, not generic advice.',
    bullets: [
      'Contextual answers based on your personal data',
      'Pre-session pep talks and post-session analysis',
      'Advice on nutrition, supplements & recovery',
      'Natural conversation — no rigid commands needed',
    ],
    video: '/assets/videos2/ai_chat_video.webm',
    image: '/assets/images2/ai_message_screen.png',
    color: '#40CC88',
  },
  {
    id: 'nutrition',
    icon: '🥗',
    title: 'Nutrition & Meal Plans',
    tagline: 'Fuel your performance, not just your macros.',
    description:
      'FitMind generates a full weekly nutrition plan matched to your calorie targets, dietary preferences and training intensity. Track meals with the food diary and get supplement recommendations tailored to your goals.',
    bullets: [
      'AI-generated weekly meal plans',
      'Daily food diary with calorie and macro tracking',
      'Diet type support: standard, keto, vegetarian, vegan & more',
      'Supplement stack recommendations',
    ],
    video: '/assets/videos2/nutrition_switching_meal_video.webm',
    image: '/assets/images2/nutrition_screen_v2.png',
    color: '#F59E0B',
  },
  {
    id: 'progress',
    icon: '📈',
    title: 'Progress Tracking',
    tagline: 'See exactly how far you\'ve come.',
    description:
      'Track weight trends, body measurements, exercise PRs and progress photos all in one place. Interactive charts show your trajectory and the AI highlights what\'s working — so when monthly recalibration comes, you can see exactly how far you\'ve come.',
    bullets: [
      'Weight trend charts with moving averages',
      'Body measurement log (chest, waist, hips, arms)',
      'Progress photo timeline with side-by-side comparisons',
      'Per-exercise strength history and PR tracking',
    ],
    video: null,
    image: '/assets/images2/progress_screen.png',
    color: '#8B5CF6',
  },
];

const Features: React.FC = () => {
  const [active, setActive] = useState(0);
  const feat = features[active];

  return (
    <section className="features" id="features">
      <div className="container">
        <div className="features__header">
          <span className="section-label">Features</span>
          <h2 className="section-title">
            Everything you need to<br /><span>train smarter</span>
          </h2>
          <p className="section-subtitle">
            From your very first workout to your most advanced training block —
            FitMind AI has the tools to keep you progressing.
          </p>
        </div>

        {/* Tab pills */}
        <div className="features__tabs">
          {features.map((f, i) => (
            <button
              key={f.id}
              className={`features__tab${i === active ? ' features__tab--active' : ''}`}
              style={i === active ? { '--tab-color': f.color } as React.CSSProperties : {}}
              onClick={() => setActive(i)}
            >
              <span className="features__tab-icon">{f.icon}</span>
              {f.title}
            </button>
          ))}
        </div>

        {/* Content panel */}
        <div className="features__panel" key={feat.id}>
          <div className="features__panel-content">
            <p className="features__tagline">{feat.tagline}</p>
            <p className="features__description">{feat.description}</p>
            <ul className="features__bullets">
              {feat.bullets.map((b, i) => (
                <li key={i}>
                  <span className="features__check">✓</span>
                  {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="features__panel-visual">
            <div className="features__phone-wrap">
              <div
                className="features__phone-glow"
                style={{ background: `radial-gradient(circle, ${feat.color}33 0%, transparent 70%)` }}
              />
              <div className="features__phone">
                <div className="features__phone-notch" />
                {feat.video ? (
                  <video
                    key={feat.video}
                    className="features__phone-video"
                    src={feat.video}
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                ) : (
                  <img
                    key={feat.image}
                    className="features__phone-video"
                    src={feat.image}
                    alt={feat.title}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
